import {useElementOffset} from '~/composables/dom/useElementOffset'
import {useElementSize} from '~/composables/dom/useElementSize'
import {useMediaFrameElements} from '~/composables/studio/useMediaFrameElements'
import type {MediaVideoCaptureOptions} from '~/composables/studio/useMediaVideoCapture'

const {
  getFrameDocument,
  getStageCanvas,
  getCompositeCanvasHeight,
  getScaleRatio,
  overlayRelativeMargin,
  computeCanvasSizeProps,
  baseStageCanvasControllerHeight,
  baseStageCanvasWidth,
} = useMediaFrameElements()

export function doTickComposition(
    mediaFrame: HTMLIFrameElement,
    targetCanvas: HTMLCanvasElement,
    videoParams: {
      mousePosition: { x: number, y: number } | null,
      cursorImage: HTMLImageElement,
      recordingStartDate: Date,
      initializeOptions: MediaVideoCaptureOptions | null,
      stampImage: HTMLImageElement
    } | null = null,
) {

  // This function must be called on `tick`, otherwise some elements might not be drawn/visible

  const ctx = targetCanvas.getContext('2d')

  if (!ctx) {
    console.warn('No 2D context available')
    return
  }

  const frameDocument = getFrameDocument(mediaFrame)
  const stageCanvas = getStageCanvas(mediaFrame)

  if (!frameDocument || !stageCanvas) {
    console.warn('No stage canvas')
    return
  }

  // Background (and clear)
  ctx.fillStyle = 'white'
  ctx.fillRect(0, 0, stageCanvas.width, stageCanvas.height)

  const stageCanvasOffset = useElementOffset(stageCanvas)
  const canvasDisplayRatio = useElementSize(stageCanvas).width / stageCanvas.width

  let previousAlpha

  // Sources
  const compositionSources: NodeListOf<HTMLCanvasElement | HTMLVideoElement> = frameDocument.querySelectorAll('canvas, video')
  for (let i = 0; i < compositionSources.length; i++) {
    const source = compositionSources[i]

    // We skip hidden elements :)
    if (source.style.display === 'none') continue

    const isThree = source.id.indexOf('EMThree') === 0

    previousAlpha = ctx.globalAlpha

    const itemOpacity = parseFloat(window.getComputedStyle(source).getPropertyValue('opacity'))
    const parentOpacity = source.parentElement ? parseFloat(window.getComputedStyle(source.parentElement).getPropertyValue('opacity')) : 1
    const opacity = itemOpacity * parentOpacity

    ctx.globalAlpha = opacity

    if (opacity === 0) {
      ctx.globalAlpha = previousAlpha
      continue
    }

    if (source.tagName.toLowerCase() === 'video') {
      const videoOffset = useElementOffset(source)

      const elementSize = useElementSize(source)

      const displayWidth = elementSize.width / canvasDisplayRatio
      const displayHeight = elementSize.height / canvasDisplayRatio
      const displayRatio = displayWidth / displayHeight // This ratio will help us crop correctly

      const actualWidth = (source as HTMLVideoElement).videoWidth
      const actualHeight = (source as HTMLVideoElement).videoHeight
      const computedHeight = actualWidth / displayRatio // This is what is actually shown to the user in the webcam floating window

      // The idea here is to vertically crop the video to only get a ratio matching the media size (without its controller)

      const sx = 0
      const sy = (actualHeight - computedHeight) / 2 // The top part we don't want
      const sWidth = actualWidth
      const sHeight = computedHeight // The actual height we want
      const dx = (videoOffset.left - stageCanvasOffset.left) / canvasDisplayRatio
      const dy = (videoOffset.top - stageCanvasOffset.top) / canvasDisplayRatio

      ctx.drawImage(source, sx, sy, sWidth, sHeight, dx, dy, displayWidth, displayHeight)
    } else if (isThree) {
      const threeOffset = useElementOffset(source)

      ctx.drawImage(
          source,
          threeOffset.left - stageCanvasOffset.left,
          threeOffset.top - stageCanvasOffset.top,
          source.width / canvasDisplayRatio, source.height / canvasDisplayRatio,
      )
    } else {
      ctx.drawImage(source, 0, 0)
    }

    ctx.globalAlpha = previousAlpha
  }

  if (!videoParams) {
    return
  }

  const {
    mousePosition,
    cursorImage,
    recordingStartDate,
    initializeOptions,
    stampImage,
  } = videoParams

  const scaleRatio = getScaleRatio(stageCanvas)
  let toBaseRatio

  const previousImageSmoothingQuality = ctx.imageSmoothingQuality
  ctx.imageSmoothingQuality = 'high'

  if (mousePosition) {
    const cursorImageSize = {width: 306, height: 489}
    const baseCursorWidth = 16
    toBaseRatio = cursorImageSize.width / baseCursorWidth

    ctx.drawImage(
        cursorImage,
        mousePosition.x / canvasDisplayRatio,
        mousePosition.y / canvasDisplayRatio,
        baseCursorWidth * scaleRatio,
        cursorImageSize.height / toBaseRatio * scaleRatio,
    )
  }

  // Stamp (name and logo overlay)

  const milli = new Date().getTime() - recordingStartDate.getTime()
  const from = 2500
  const to = 3000

  let ratio
  if (milli < from) {
    ratio = 0
  } else if (milli >= from && milli <= to) {
    ratio = (milli - from) / (to - from)
  } else {
    ratio = 1
  }

  if (ratio < 1) {
    previousAlpha = ctx.globalAlpha
    ctx.globalAlpha = 1 - ratio
    const nameSize = 10 * scaleRatio
    ctx.fillStyle = 'black'
    ctx.textAlign = 'right'
    ctx.font = `${nameSize}px sans-serif`
    const textAbsoluteMargin = overlayRelativeMargin * scaleRatio
    ctx.fillText(
        useUserStore().stringRepresentation || initializeOptions?.directoryOwnerStringRepresentation || 'No user',
        stageCanvas.width - textAbsoluteMargin,
        getCompositeCanvasHeight(stageCanvas) - textAbsoluteMargin,
    )
    ctx.globalAlpha = previousAlpha
  }

  if (ratio > 0) {
    previousAlpha = ctx.globalAlpha
    ctx.globalAlpha = ratio

    const stampImageSize = {width: 526, height: 128}
    const baseStampWidth = 100
    const baseStampHeight = baseStampWidth * (stampImageSize.height / stampImageSize.width)
    const stampAbsoluteWidth = baseStampWidth * scaleRatio

    toBaseRatio = stampImageSize.width / baseStampWidth
    const stampAbsoluteHeight = stampImageSize.height / toBaseRatio * scaleRatio

    ctx.drawImage(
        stampImage,
        (baseStageCanvasWidth - baseStampWidth - overlayRelativeMargin) * scaleRatio,
        (computeCanvasSizeProps(stageCanvas).baseStageCanvasHeight - baseStageCanvasControllerHeight - baseStampHeight - overlayRelativeMargin) * scaleRatio,
        stampAbsoluteWidth,
        stampAbsoluteHeight,
    )

    ctx.globalAlpha = previousAlpha
  }

  ctx.imageSmoothingQuality = previousImageSmoothingQuality
}